import React from 'react';
import { ButtonProps as SiteCoreButtonProps } from '@alliander-fe/sitecore-types';
import { useEffect } from 'react';
import { ConclusionBlockProps } from '@ads-core/types';
import {
  FollowUpStepsSitecore,
  Box,
  ConclusionBlock,
  PageGrid,
  GridItem,
  Heading,
  Loader,
} from '@ads-core/components';
import { scrollToElement } from '@ads-core/utils';
import { useAdsContext } from '@ads-core/providers';
import { CongestionCheckerProps } from 'components/CongestionChecker';
import { useParams } from 'src/hooks/useParams';
import { useQuery } from '@tanstack/react-query';
import { IntegrationApi } from '@alliander-fe/api';
import { formatPostalCode } from 'src/utils/format';
import { getFeedbackAndConclusion, removeDuplicateData, setShortageData } from './utils/utils';
import { setCongestionFollowUpSteps } from './utils/setCongestionFollowUpSteps';

import { Notification } from './_Notification';
import { hasCongestions } from './utils/hasCongestions';
import { getLatestEndDate } from './utils/getLatestEndDate';
import { CongestionFeedback } from './_CongestionFeedback';
import { useCongestionReports } from './hooks/useCongestionReports';

export const CongestionCheck = (props: CongestionCheckerProps['fields']) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const refCongestion = React.useRef<HTMLDivElement>(null);
  const refConclusion = React.useRef<HTMLDivElement>(null);
  const adsContext = useAdsContext();

  const { postalCode } = useParams();
  const displayPostalCode = formatPostalCode(postalCode);

  const query = useQuery({
    enabled: Boolean(postalCode),
    queryKey: ['congestion-check', postalCode],
    refetchOnWindowFocus: false,
    queryFn: () => IntegrationApi.congestionEndpointsGetCongestions({ PostalCode: postalCode }),
  });

  const fetchStatus = query.fetchStatus;
  const status = query.status;
  const consume = query.data?.directions?.consume;
  const supply = query.data?.directions?.supply;
  const isNetworkProvider = Boolean(consume || supply);

  const shortageData = setShortageData(consume, supply);
  const filteredCongestionData = removeDuplicateData(shortageData);
  const congestions = hasCongestions(query.data);

  const { reports, isLoading } = useCongestionReports(filteredCongestionData);
  const isCongestionLoading = isLoading || fetchStatus === 'fetching';

  // Create the step data
  const followUpSteps = setCongestionFollowUpSteps({
    congestions: filteredCongestionData,
    reports,
    congestionReportsWildcardPage: props?.congestionReportsWildcardPage,
    isLoading: isCongestionLoading,
    enableBandWidth: props?.enableAdditionalInfo?.value ?? false,
  });

  const latestEndDate = getLatestEndDate(filteredCongestionData);

  // Get the feedback and conclusion text
  const { feedback, conclusion } = getFeedbackAndConclusion({
    props: props,
    replaceText: {
      '{postalCode}': displayPostalCode,
      '{congestionAmount}': `${
        followUpSteps.length === 1
          ? `${followUpSteps.length} knelpunt`
          : `${followUpSteps.length} knelpunten`
      }`,
      '{latestEndDate}': latestEndDate.latestEndDate,
      '{latestEndDateConsume}': latestEndDate.latestEndDateConsume,
      '{latestEndDateSupply}': latestEndDate.latestEndDateSupply,
    },
    ...congestions,
  });

  useEffect(() => {
    if (query.isFetched && ref.current) {
      scrollToElement(ref, adsContext.headerHeight);
    }
  }, [adsContext.headerHeight, query.isFetched]);

  return (
    <Box ref={ref}>
      <Notification isNetworkProvider={isNetworkProvider} status={status} />
      {isCongestionLoading ? (
        <PageGrid>
          <GridItem columnStart={{ initial: '1', lg: '3' }} columnEnd={{ initial: '-1', lg: '-3' }}>
            <Heading size="h2">
              Laden... <Loader />
            </Heading>
          </GridItem>
        </PageGrid>
      ) : null}

      {!isCongestionLoading && feedback && isNetworkProvider ? (
        <CongestionFeedback
          consume={congestions.consume}
          supply={congestions.supply}
          feedback={feedback}
          congestionText={props?.congestionText?.value ?? ''}
          congestions={Boolean(followUpSteps?.length)}
          handlePrimaryButton={(value) =>
            scrollToElement(
              value === 'conclusion' ? refConclusion : refCongestion,
              adsContext.headerHeight
            )
          }
          handleSecondaryButton={(value) =>
            scrollToElement(
              value === 'conclusion' ? refConclusion : refCongestion,
              adsContext.headerHeight
            )
          }
        />
      ) : null}

      {followUpSteps?.length && isNetworkProvider ? (
        <Box ref={refCongestion}>
          <FollowUpStepsSitecore
            followUpSteps={followUpSteps}
            conclusion={<ConclusionBlock {...conclusion} />}
          />
        </Box>
      ) : null}

      {!followUpSteps?.length && conclusion && isNetworkProvider ? (
        <PageGrid>
          <GridItem columnStart={{ initial: '1', lg: '2' }} columnEnd={{ initial: '-1', lg: '-2' }}>
            <Box width="100%" paddingTop={12} ref={refConclusion}>
              <ConclusionBlock {...conclusion} />
            </Box>
          </GridItem>
        </PageGrid>
      ) : null}
    </Box>
  );
};

export type CongestionProps = {
  latestEndDate: string;
  latestEndDateSupply: string;
  latestEndDateConsume: string;
  name?: string;
  level: {
    supply?: string;
    consume?: string;
  };
};

type CongestionFeedbackProps = {
  description: string;
  buttonPrimary?: SiteCoreButtonProps;
  buttonSecondary?: SiteCoreButtonProps;
};

export type FeedbackAndConclusionProps = {
  feedback: CongestionFeedbackProps;
  conclusion?: ConclusionBlockProps;
};
